/**
 * EventManager
 *
 * Runtime execution is owned by Nest. This manager keeps renderer-side
 * event readers plus narrow task metadata mutations backed by the active
 * storage driver.
 */

import type { HarnessStreamEvent, HarnessId } from "../../providerEvents/harnessEventTypes"
import type { ActionEvent } from "../../types"
import type { CodeStore } from "../store"
import { harnessIdFromSessionRef } from "../taskRecordAdapter"

export class EventManager {
    constructor(private store: CodeStore) {}

    /**
     * Check if an action event failed due to defunct session (forking from expired session).
     * Returns the defunct session ID if found, or null if no defunct session error.
     */
    getDefunctSessionId(event: ActionEvent): string | null {
        const stderrText = event.execution.events
            .filter((e): e is HarnessStreamEvent & { type: "stderr"; direction: "execution" } => e.direction === "execution" && e.type === "stderr")
            .map((e) => e.data)
            .join(" ")

        const errorText = event.execution.events
            .filter((e): e is HarnessStreamEvent & { type: "error"; direction: "execution" } => e.direction === "execution" && e.type === "error")
            .map((e) => e.error)
            .join(" ")

        const resultErrors: string[] = []
        for (const e of event.execution.events) {
            if (e.direction === "execution" && (e.type === "raw_message" || (e.type as string) === "sdk_message")) {
                const msg = (e as Record<string, unknown>).message as { type?: string; errors?: string[] }
                if (msg?.type === "result" && msg.errors) {
                    resultErrors.push(...msg.errors)
                }
            }
        }

        const combined = stderrText + " " + errorText + " " + resultErrors.join(" ")
        const match = combined.match(/no conversation found with session id[:\s]+([a-f0-9-]+)/i)
        if (match) return match[1]

        const lower = combined.toLowerCase()
        if (lower.includes("no conversation found") || (lower.includes("session") && lower.includes("not found"))) {
            return event.execution.parentSessionId || null
        }

        return null
    }

    /** Check if an action event failed due to defunct session (forking from expired session) */
    hasDefunctSessionError(event: ActionEvent): boolean {
        return this.getDefunctSessionId(event) !== null
    }

    /** Get the session ID from the last event with an execution, skipping defunct sessions */
    getLastEventSessionId(taskId: string): string | undefined {
        return this.getLastEventSessionContext(taskId)?.sessionId
    }

    /** Get the newest persisted session context (session ID + harness + model). */
    getLastEventSessionContext(taskId: string): { sessionId: string; harnessId: HarnessId; modelId?: string } | undefined {
        const sessions = this.store.productState?.tasks.get(taskId)?.sessions ?? []
        const session = sessions
            .filter((candidate) => !!candidate.nestSessionId)
            .sort((a, b) => (a.updatedAt ?? a.createdAt ?? "").localeCompare(b.updatedAt ?? b.createdAt ?? ""))
            .at(-1)
        if (!session?.nestSessionId) return undefined
        return {
            sessionId: session.nestSessionId,
            harnessId: harnessIdFromSessionRef(session),
            modelId: session.model,
        }
    }
}
